import {
  createLastNWindow,
  createRelativeWindow,
  DateTrunc,
  DateWindow,
  getUnitsinHours,
  getUnitsInMinutes,
  getUnitsInSeconds,
  isLastNWindow,
  isLatestDataWindow,
  isValidRelativeDateRangeInput,
  MAXIMUM_RELATIVE_UNITS,
  MINIMUM_RELATIVE_UNITS,
} from '@tableau/metrics-engine';
import { TextFieldWidget } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../../../gen-src/Localize';
import { shakeInputIfInvalid } from '../../../shakeIfInvalid/shakeInputIfInvalid';

export interface DateWindowRelativeDateComponentProps {
  testId: string;
  dateTrunc: DateTrunc;
  onDateWindowChange: (value: DateWindow | null) => void;
  selectedDateWindow: DateWindow | null;
  validationCounter?: number;
  disabled?: boolean;
}

export const DateWindowRelativeDateComponent = (props: DateWindowRelativeDateComponentProps): JSX.Element => {
  const startingUnits = unitsToStartWith(props.selectedDateWindow, props.dateTrunc);
  const [hasBeenEdited, setHasBeenEdited] = React.useState(false);
  const [relativeUnits, setRelativeUnits] = React.useState<number | null>(startingUnits);

  React.useEffect(() => {
    props.onDateWindowChange(relativeUnits ? createWindow(relativeUnits) : null);
  }, [relativeUnits]);

  const createWindow = (numberOfUnits: number): DateWindow => {
    if (props.dateTrunc === DateTrunc.UNKNOWN) {
      return createLastNWindow(numberOfUnits);
    } else {
      return createRelativeWindow({
        unit: props.dateTrunc,
        numberOfUnits,
      });
    }
  };

  const onBlur = () => {
    setHasBeenEdited(true);
  };

  const handleChange = (value: string) => {
    setHasBeenEdited(true);
    const parsed = parseInt(value, 10);
    const finalValue = isNaN(parsed) ? null : parsed;
    setRelativeUnits(finalValue);
  };

  const invalid = hasBeenEdited && !isValidRelativeDateRangeInput(relativeUnits);
  const inputRef = React.createRef<HTMLInputElement>();
  React.useEffect(shakeInputIfInvalid(invalid, inputRef), [props.validationCounter]);

  return (
    <>
      <TextFieldWidget
        {...{
          ref: inputRef,
          kind: 'outline',
          value: relativeUnits ?? '',
          onChange: (e) => handleChange(e.target.value),
          onBlur: onBlur,
          valid: invalid ? false : undefined,
          message: invalid ? Messages.RelativeDateWindowInputErrorMessage() : <br />,
          testId: props.testId,
          label: '',
          min: MINIMUM_RELATIVE_UNITS,
          max: MAXIMUM_RELATIVE_UNITS,
          disabled: props.disabled,
        }}
      />
    </>
  );
};

const unitsToStartWith = (window: DateWindow | null, metricDateTrunc: DateTrunc): number => {
  if (window === null) {
    return 1;
  }
  if (isLatestDataWindow(window)) {
    const units = window.duration.numberOfUnits;
    if (window.duration.unit !== metricDateTrunc) {
      if (window.duration.unit === DateTrunc.MINUTE) {
        return getUnitsInSeconds(window.duration.unit, units);
      } else if (window.duration.unit === DateTrunc.HOUR) {
        return getUnitsInMinutes(window.duration.unit, units);
      } else if (window.duration.unit === DateTrunc.DAY) {
        return getUnitsinHours(window.duration.unit, units);
      }
    }

    return units;
  }
  if (isLastNWindow(window)) {
    return window.n;
  }

  return 1;
};
